import { useMemo } from 'react';
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { ApiClient } from '../../shared/api/ApiClient.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import { fetchPlatformAccountJson } from './platformAccountFetch.js';
import { PLATFORM_ACCOUNT_ORDERS_PATH } from '../orders/ordersApi.js';

/** Wave E — absolute platform checkout-session paths (incomplete list + buy-again). */
export const PLATFORM_ACCOUNT_CHECKOUT_SESSIONS_PATH =
  '/api/v1/customer-account/checkout-sessions';

export const PLATFORM_ACCOUNT_INCOMPLETE_SESSIONS_PATH =
  `${PLATFORM_ACCOUNT_CHECKOUT_SESSIONS_PATH}/incomplete`;

export interface IncompleteCheckoutSession {
  sessionId: string;
  /** Legacy alias — BE emits `salesPointId`; FE maps both. */
  kioskId: number | null;
  abandonedAt: string;
  /** Owning shop slug — required for platform hub Resume → `/{shop}/checkout`. */
  shopTenantCode?: string;
  /**
   * Cheap buy-again validate gates (no stock scan). Absent/false → hide Resume (G6 fail-closed).
   */
  resumeEligible?: boolean;
}

type IncompleteSessionWire = {
  sessionId: string;
  abandonedAt: string;
  salesPointId?: number | null;
  kioskId?: number | null;
  shopTenantCode?: string;
  resumeEligible?: boolean;
};

function mapIncompleteSessions(raw: {
  sessions: IncompleteSessionWire[];
}): { sessions: IncompleteCheckoutSession[] } {
  return {
    sessions: raw.sessions.map((s) => ({
      sessionId: s.sessionId,
      kioskId: s.kioskId ?? s.salesPointId ?? null,
      abandonedAt: s.abandonedAt,
      ...(s.shopTenantCode !== undefined ? { shopTenantCode: s.shopTenantCode } : {}),
      ...(s.resumeEligible !== undefined ? { resumeEligible: s.resumeEligible } : {}),
    })),
  };
}

export interface BuyAgainAvailability {
  availableLines: Array<{
    productId: number;
    variantId: number | null;
    quantity: number;
  }>;
  unavailableLines: Array<{
    productId: number;
    variantId: number | null;
    quantity: number;
  }>;
}

export interface BuyAgainStartResult {
  newSessionId: string;
  /** Owning shop slug from BE (G7 — required for platform-hub checkout nav). */
  tenantCode?: string;
  trimmedLines?: Array<{
    productId: number;
    variantId: number | null;
    quantity: number;
  }>;
}

export interface BuyAgainRequestContext {
  /** Owning shop slug from the order / session row (required under platform `/account`). */
  shopTenantCode?: string;
  /** Prior order / session currency (ISO 4217) — G8 fail-closed when omitted on order start. */
  currency?: string;
}

function tenantScopedRequestOptions(
  shopTenantCode: string | undefined,
): { tenantCode?: string } {
  const trimmed = shopTenantCode?.trim() ?? '';
  return hasCustomerApiTenant(trimmed) ? { tenantCode: trimmed } : {};
}

export function createAccountCheckoutApi(deps: {
  api: ApiClient;
  apiBaseUrl: string;
  platformChrome: boolean;
}) {
  const { api, apiBaseUrl, platformChrome } = deps;

  return {
    /**
     * Incomplete list — tenant-scoped or Wave E absolute fan-out on platform chrome.
     */
    fetchIncompleteSessions: async (): Promise<{ sessions: IncompleteCheckoutSession[] }> => {
      if (platformChrome) {
        const data = await fetchPlatformAccountJson<{ sessions: IncompleteSessionWire[] }>({
          apiBaseUrl,
          path: PLATFORM_ACCOUNT_INCOMPLETE_SESSIONS_PATH,
        });
        return mapIncompleteSessions(data);
      }
      const data = await api.request<{ sessions: IncompleteSessionWire[] }>(
        '/customer-account/checkout-sessions/incomplete',
      );
      return mapIncompleteSessions(data);
    },

    startOrderBuyAgain: (
      transactionId: number,
      context: BuyAgainRequestContext = {},
    ): Promise<BuyAgainStartResult> => {
      const body =
        context.currency !== undefined ? { currency: context.currency } : undefined;
      if (platformChrome) {
        return fetchPlatformAccountJson<BuyAgainStartResult>({
          apiBaseUrl,
          path: `${PLATFORM_ACCOUNT_ORDERS_PATH}/${transactionId}/buy-again`,
          method: 'POST',
          ...(body !== undefined ? { body } : {}),
        });
      }
      return api.request(`/customer-account/orders/${transactionId}/buy-again`, {
        method: 'POST',
        ...(body !== undefined ? { body } : {}),
        ...tenantScopedRequestOptions(context.shopTenantCode),
      });
    },

    validateOrderBuyAgain: (
      transactionId: number,
      context: BuyAgainRequestContext = {},
    ): Promise<BuyAgainAvailability> => {
      if (platformChrome) {
        return fetchPlatformAccountJson<BuyAgainAvailability>({
          apiBaseUrl,
          path: `${PLATFORM_ACCOUNT_ORDERS_PATH}/${transactionId}/buy-again/validate`,
          method: 'POST',
        });
      }
      return api.request(`/customer-account/orders/${transactionId}/buy-again/validate`, {
        method: 'POST',
        ...tenantScopedRequestOptions(context.shopTenantCode),
      });
    },

    startSessionBuyAgain: (
      sessionId: string,
      context: BuyAgainRequestContext = {},
    ): Promise<BuyAgainStartResult> => {
      const encoded = encodeURIComponent(sessionId);
      const body =
        context.currency !== undefined ? { currency: context.currency } : undefined;
      if (platformChrome) {
        return fetchPlatformAccountJson<BuyAgainStartResult>({
          apiBaseUrl,
          path: `${PLATFORM_ACCOUNT_CHECKOUT_SESSIONS_PATH}/${encoded}/buy-again`,
          method: 'POST',
          ...(body !== undefined ? { body } : {}),
        });
      }
      return api.request(
        `/customer-account/checkout-sessions/${encoded}/buy-again`,
        {
          method: 'POST',
          ...(body !== undefined ? { body } : {}),
          ...tenantScopedRequestOptions(context.shopTenantCode),
        },
      );
    },

    validateSessionBuyAgain: (
      sessionId: string,
      context: BuyAgainRequestContext = {},
    ): Promise<BuyAgainAvailability> => {
      const encoded = encodeURIComponent(sessionId);
      if (platformChrome) {
        return fetchPlatformAccountJson<BuyAgainAvailability>({
          apiBaseUrl,
          path: `${PLATFORM_ACCOUNT_CHECKOUT_SESSIONS_PATH}/${encoded}/buy-again/validate`,
          method: 'POST',
        });
      }
      return api.request(
        `/customer-account/checkout-sessions/${encoded}/buy-again/validate`,
        {
          method: 'POST',
          ...tenantScopedRequestOptions(context.shopTenantCode),
        },
      );
    },
  };
}

export type AccountCheckoutApi = ReturnType<typeof createAccountCheckoutApi>;

export function useAccountCheckoutApi(): AccountCheckoutApi {
  const api = useApiClient();
  const config = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const platformChrome = isPlatformChromeTenant(tenantCode);
  return useMemo(
    () =>
      createAccountCheckoutApi({
        api,
        apiBaseUrl: config.apiBaseUrl,
        platformChrome,
      }),
    [api, config.apiBaseUrl, platformChrome],
  );
}

export function useBuyAgainSwitchGate(): () => Promise<void> {
  return useMemo(
    () => async (): Promise<void> => Promise.resolve(),
    [],
  );
}

/**
 * Incomplete sessions — enabled on tenant org account and platform hub (`__platform__`).
 * Platform uses absolute Wave E fan-out; never tenant-prefix under platform identity.
 */
export function useIncompleteCheckoutSessions(): UseQueryResult<{
  sessions: IncompleteCheckoutSession[];
}> {
  const tenantCode = useTenantCode();
  const accountCheckoutApi = useAccountCheckoutApi();
  const platformChrome = isPlatformChromeTenant(tenantCode);
  return useQuery({
    queryKey: ['incomplete-checkout-sessions', tenantCode],
    queryFn: () => accountCheckoutApi.fetchIncompleteSessions(),
    enabled: platformChrome || hasCustomerApiTenant(tenantCode),
  });
}

export function useStartOrderBuyAgain(): UseMutationResult<
  BuyAgainStartResult,
  Error,
  { transactionId: number; shopTenantCode?: string }
> {
  const accountCheckoutApi = useAccountCheckoutApi();
  const ensureBuyAgainAuth = useBuyAgainSwitchGate();
  return useMutation({
    mutationFn: async ({ transactionId, shopTenantCode }) => {
      await ensureBuyAgainAuth();
      return accountCheckoutApi.startOrderBuyAgain(transactionId, { shopTenantCode });
    },
  });
}

export function useStartSessionBuyAgain(): UseMutationResult<
  BuyAgainStartResult,
  Error,
  { sessionId: string; shopTenantCode?: string }
> {
  const accountCheckoutApi = useAccountCheckoutApi();
  const ensureBuyAgainAuth = useBuyAgainSwitchGate();
  return useMutation({
    mutationFn: async ({ sessionId, shopTenantCode }) => {
      await ensureBuyAgainAuth();
      return accountCheckoutApi.startSessionBuyAgain(sessionId, { shopTenantCode });
    },
  });
}
